import { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useGameStore } from '../stores/gameStore';
import { SOCKET_EVENTS } from '../utils/constants';

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
	const [socket, setSocket] = useState(null);
	const [connected, setConnected] = useState(false);
	const setPlayerId = useGameStore((state) => state.setPlayerId);
	const setSocketId = useGameStore((state) => state.setSocketId);

	useEffect(() => {
		// Create Socket.io connection
		const newSocket = io(process.env.REACT_APP_API_URL, {
			transports: ['websocket', 'polling'], // Allow fallback to polling if websocket fails
			autoConnect: true,
			reconnection: true, // Enable reconnection
			reconnectionAttempts: 1, // Try to reconnect 10 times
			reconnectionDelay: 1000, // Start with 1s delay
			reconnectionDelayMax: 5000, // Maximum delay between reconnections
			timeout: 20000, // Longer timeout
		});

		// Connection events
		newSocket.on('connect', () => {
			console.log('Connected to server');
			setConnected(true);

			// This is the application-level handshake.
			// It runs on initial connection AND on every successful reconnection.
			const localStoragePlayerId = localStorage.getItem('triviaPlayerId');
			const gameId = localStorage.getItem('triviaGameId'); // Also need the gameId for game-specific reconnects

			if (localStoragePlayerId && gameId) {
				// If we have a persistent ID and were in a game, attempt to rejoin.
				console.log(`Attempting to rejoin game ${gameId} as player ${localStoragePlayerId}`);
				newSocket.emit(SOCKET_EVENTS.RECONNECT_ATTEMPT, { gameId, playerId: localStoragePlayerId });
			} else {
				// Otherwise, register a new session.
				console.log('Attempting to register a new session...');
				newSocket.emit(SOCKET_EVENTS.REGISTER_SESSION, { localStoragePlayerId });
			}
		});

		// This event confirms our identity from the server
		newSocket.on(SOCKET_EVENTS.SESSION_ESTABLISHED, ({ socketId, playerId }) => {
			console.log(`Session established. My Player ID is ${playerId}`);
			localStorage.setItem('triviaPlayerId', playerId);
			setPlayerId(playerId);
			setSocketId(socketId);
		});

		newSocket.on('disconnect', (reason) => {
			console.log('Disconnected from server. Reason:', reason);
			setConnected(false);
		});

		newSocket.on('connect_error', (error) => {
			console.error('Connection error:', error);
			setConnected(false);
		});

		newSocket.on('reconnect', (attemptNumber) => {
			console.log(`Reconnected to server after ${attemptNumber} attempts`);
			setConnected(true);
		});

		newSocket.on('reconnect_attempt', (attemptNumber) => {
			console.log(`Reconnect attempt #${attemptNumber}`);
		});

		newSocket.on('reconnect_error', (error) => {
			console.error('Error when trying to reconnect:', error);
		});

		newSocket.on('reconnect_failed', () => {
			console.error('Failed to reconnect after all attempts');
		});

		setSocket(newSocket);

		// --- TEMPORARY FOR DDEPURATION ---
		if (process.env.NODE_ENV === 'development') {
			window.socket = newSocket;
		}

		// Clean up on unmount
		return () => {
			newSocket.disconnect();
		};
	}, []); // Removed dependencies to ensure this effect runs only once

	return <SocketContext.Provider value={{ socket, connected }}>{children}</SocketContext.Provider>;
};
